/**
 * geoxMcpClient — GEOX MCP Client Adapter (minimal stub)
 * ═══════════════════════════════════════════════════════════════════════════════
 * DITEMPA BUKAN DIBERI
 *
 * Provides the OperatorCockpit with session binding and tool call routing
 * through the host window's postMessage bridge or direct HTTP fetch.
 */

export interface GeoxSessionIdentity {
  actorId: string;
  sessionId: string;
  expiresAt: string | null;
}

export interface GeoxSurfaceStatus {
  public_count?: number;
  callable_tools?: number;
  registered_tools?: number;
  organ?: string;
  version?: string;
  status?: string;
}

export interface GeoxToolInvocation {
  tool: string;
  arguments: Record<string, unknown>;
}

const GEOX_HEALTH_URL = '/health';
const GEOX_MCP_URL = '/mcp/';

class GeoxMcpClient {
  private _sessionToken: string | null = null;
  private _connected = false;

  get sessionToken(): string | null {
    return this._sessionToken;
  }

  get connected(): boolean {
    return this._connected;
  }

  async connect(): Promise<GeoxSurfaceStatus> {
    try {
      const resp = await fetch(GEOX_HEALTH_URL, {
        headers: { Accept: 'application/json' },
      });
      if (!resp.ok) throw new Error(`GEOX health check failed: ${resp.status}`);
      const health = await resp.json();
      this._connected = true;
      return {
        status: health.status ?? 'live',
        version: health.version ?? health.release_name ?? 'unknown',
        public_count: health.public_count ?? health.callable_tools ?? 39,
        registered_tools: health.registered_tools ?? 39,
        callable_tools: health.callable_tools ?? health.public_count ?? 39,
        organ: 'geox',
      };
    } catch (err) {
      this._connected = false;
      throw err;
    }
  }

  async bindSession(token: string): Promise<GeoxSurfaceStatus> {
    this._sessionToken = token;
    return this.connect();
  }

  async callTool(invocation: GeoxToolInvocation): Promise<unknown> {
    const body = JSON.stringify({
      jsonrpc: '2.0',
      id: `${invocation.tool}-${Date.now()}`,
      method: 'tools/call',
      params: { name: invocation.tool, arguments: invocation.arguments },
    });

    const resp = await fetch(GEOX_MCP_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json, text/event-stream',
      },
      body,
    });

    if (!resp.ok) {
      throw new Error(`GEOX MCP HTTP ${resp.status}: ${resp.statusText}`);
    }

    const json = await resp.json();
    if (json.error) {
      throw new Error(`GEOX MCP error: ${json.error.message ?? JSON.stringify(json.error)}`);
    }

    const content = json?.result?.content;
    if (content?.[0]?.text) {
      try {
        return { result: JSON.parse(content[0].text) };
      } catch {
        return { result: content[0].text };
      }
    }

    return json?.result ?? null;
  }

  clearSession(): void {
    this._sessionToken = null;
    this._connected = false;
  }
}

export const geoxMcpClient = new GeoxMcpClient();

/**
 * Inspect a session capability token (client-side only — no crypto verify).
 */
export function inspectSessionToken(token: string): GeoxSessionIdentity {
  try {
    // Try to decode the SCT payload (base64url middle segment)
    const parts = token.split('.');
    if (parts.length >= 2) {
      const payload = JSON.parse(
        decodeURIComponent(
          atob(parts[1].replace(/-/g, '+').replace(/_/g, '/'))
            .split('')
            .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
            .join('')
        )
      );
      return {
        actorId: payload.actor || payload.actor_id || 'unknown',
        sessionId: payload.sid || payload.session_id || 'unknown',
        expiresAt: payload.exp ? new Date(payload.exp * 1000).toISOString() : null,
      };
    }
  } catch {
    // Fall through
  }

  return {
    actorId: 'anonymous',
    sessionId: 'anon-' + Date.now(),
    expiresAt: null,
  };
}
